/**
 * Copyright (c), Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import React, { useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Paper,
    Box,
    Tab,
    Tabs,
    Button,
    Card,
    CardContent,
    CardMedia,
    CardActions
} from '@mui/material';
import { Info, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Character3DViewer from '../components/Character3DViewer';

// Interface for character data
interface CharacterData {
    id: string;
    name: string;
    description: string;
    modelPath: string;
    thumbnailPath?: string;
}

// Character data
const CHARACTERS: CharacterData[] = [
    {
        id: 'character1',
        name: 'Character 1',
        description: 'Main character with teen style and modern clothing.',
        modelPath: '/Character1.glb',
        thumbnailPath: '/static/character_previews/teen_girl_modern.jpg'
    },
    {
        id: 'character2',
        name: 'Character 2',
        description: 'Secondary character with athletic style and casual clothing.',
        modelPath: '/Character2.glb',
        thumbnailPath: '/static/character_previews/teen_girl_athletic.jpg'
    }
];

const CharacterViewerPage: React.FC = () => {
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [selectedCharacter, setSelectedCharacter] = useState<CharacterData | null>(null);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    const handleCharacterSelect = (character: CharacterData) => {
        setSelectedCharacter(character);
    };

    const handleBackToList = () => {
        setSelectedCharacter(null);
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    // Filter characters based on selected tab
    const filteredCharacters = CHARACTERS;

    return (
        <Container maxWidth="lg">
            <Box sx={{ my: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={handleBackToHome}
                        sx={{ mr: 2 }}
                    >
                        Back to Home
                    </Button>
                    <Typography variant="h4" component="h1" gutterBottom>
                        3D Character Viewer
                    </Typography>
                </Box>

                {!selectedCharacter ? (
                    // Character list view
                    <>
                        <Paper sx={{ mb: 3 }}>
                            <Tabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="fullWidth"
                            >
                                <Tab label="All Characters" />
                                <Tab label="Main Characters" />
                                <Tab label="Supporting Characters" />
                            </Tabs>
                        </Paper>

                        <Grid container spacing={3}>
                            {filteredCharacters.map((character) => (
                                <Grid item xs={12} sm={6} md={4} key={character.id}>
                                    <Card>
                                        <CardMedia
                                            sx={{ height: 200 }}
                                            image={character.thumbnailPath || '/static/character_previews/teen_girl_modern.jpg'}
                                            title={character.name}
                                        />
                                        <CardContent>
                                            <Typography gutterBottom variant="h5" component="div">
                                                {character.name}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {character.description}
                                            </Typography>
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                size="small"
                                                onClick={() => handleCharacterSelect(character)}
                                            >
                                                View in 3D
                                            </Button>
                                            <Button size="small" startIcon={<Info />}>
                                                Details
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                ) : (
                    // Character detail view with 3D model
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Button
                                startIcon={<ArrowBack />}
                                onClick={handleBackToList}
                                sx={{ mr: 2 }}
                            >
                                Back to Characters
                            </Button>
                            <Typography variant="h5" component="h2">
                                {selectedCharacter.name}
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        height: 500,
                                        overflow: 'hidden',
                                        borderRadius: 2
                                    }}
                                >
                                    <Character3DViewer
                                        modelPath={selectedCharacter.modelPath}
                                        height="100%"
                                        backgroundColor="#f0f0f0"
                                    />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                                    <Typography variant="h6" gutterBottom>
                                        Character Information
                                    </Typography>
                                    <Typography variant="body1" paragraph>
                                        {selectedCharacter.description}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        Interact with the 3D model:
                                    </Typography>
                                    <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                                        <li>Click and drag to rotate</li>
                                        <li>Scroll to zoom in/out</li>
                                        <li>Right-click and drag to pan</li>
                                    </Typography>

                                    <Box sx={{ mt: 4 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            onClick={() => navigate('/create-script')}
                                        >
                                            Use in Script
                                        </Button>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default CharacterViewerPage;
